import type { StyleRow, CustomFieldRowEntry } from './stylesFixtureData';

/**
 * Deterministic probes for the Styles bulk-edit "preview, search, Confirm step, export validation
 * and activity log" epic (DL-66477, Testmo folder 93165 part 4 of 4 — dl-58397). All isolable
 * together via `?search=PCLProbe` (unique substring, absent from every generated/other fixture
 * name) and, per case, a further sub-token (`PCLProbe MixedPill`, `PCLProbe SearchFields`,
 * `PCLProbe Confirm`, `PCLProbe Export`, `PCLProbe Log*`) so each test narrows to exactly its own
 * rows. All rows are `state: 'unpublished'` so every bulk-editable field (Supplier included) is
 * editable and the bulk-edit warning modal never intercepts selection.
 *
 * Id range 965xx — chosen to not collide with the neighbouring bulk-edit fixtures (probes 903xx,
 * clear-fields/audit 95xxx, the sibling dl-66466 work's 964xx). Registered by `styles.ts` via its
 * OWN dedicated push (`stylesPreviewConfirmLogFixtures`), separate from `stylesBulkEditProbes` —
 * NOT spread into that shared array. That earlier approach (fixed by this file) synthesized this
 * epic's `brand` from the SAME shared `brandOptions[0]` / `contactPersons[0]` the DL-62593 field-
 * availability probes (`stylesBulkEditProbeFixtureData.ts`) also assign, which regressed
 * `field-availability/supplier-contact-advanced-cases.spec.ts` deterministically (its
 * brand-scoped supplier/contact assertions depend on that shared brand carrying ONLY its own
 * probes' contact wiring). Every field below is now a fully synthetic id/name in this file's own
 * 965xx-adjacent range (96590-96592), used nowhere else, so this epic can never again perturb a
 * sibling spec's shared fixture data — additive-only, in the true sense.
 *
 * Why REAL persisted rows (not an `apiTransform` GET override): the Preview "Mixed values" pill,
 * the Confirm step's impacted-list + changes panel, and the mock's per-style audit-log delta all
 * read the SELECTED rows' own values through the REAL save path (`PUT /api/styles` +
 * `GET /api/styles/:id/audit-log`) — an injected row would bypass the handler that records the
 * audit entry. `StyleRow`/`CustomFieldRowEntry` are imported as types only (erased at compile
 * time), so no runtime cycle with `styles.ts` (which imports these fixtures).
 */

const SEASON_AW25 = { id: 26, customId: 'AW25', name: 'AW25', project: '' };
const SEASON_AW26 = { id: 28, customId: 'AW26', name: 'AW26', project: '' };
const GROUP_MEN = { id: 71, customId: 'MEN', name: 'Menswear' };

/**
 * Synthetic, epic-exclusive brand — deliberately NOT `brandOptions[0]`/`brandsData[0]` (the brand
 * every other bulk-edit probe file assigns its rows to). No story in this epic touches the Brand
 * or Brand contact field, so this identity only needs to be internally consistent, never resolved
 * against the shared `brandOptions` list.
 */
const PROBE_BRAND = {
  id: 96590,
  customId: 'PCL-BRAND',
  name: 'PCLProbe Brand',
  contactPersonId: '96590000-0000-0000-0000-000000000000',
  contactPersonName: 'PCLProbe Brand Contact',
};

const textCf = (id: number, value: string): CustomFieldRowEntry => ({
  id,
  type: 'text',
  value: { id: 0, value },
});

/** Fit Type (CF 703, single-select AllowedValue) row entry. */
const fitTypeCf = (allowedValueId: number, label: string): CustomFieldRowEntry => ({
  id: 703,
  type: 'allowedvalue',
  value: { id: allowedValueId, value: label },
});

const base = (id: number, name: string, overrides: Partial<StyleRow> = {}): StyleRow => ({
  id,
  name,
  customId: `ST-${id}`,
  description: null,
  state: 'unpublished',
  logo: '',
  season: SEASON_AW25,
  group: GROUP_MEN,
  brand: { ...PROBE_BRAND },
  supplier: null,
  categories: [],
  styleColors: [],
  customFields: [],
  properties: [],
  lastUpdated: '2026-01-01T00:00:00',
  readyForExport: false,
  ...overrides,
});

// --- 754722 — "Mixed values" pill ONLY where the selected styles genuinely differ ------------
// Alpha/Bravo are identical in every bulk-editable dimension EXCEPT Season (AW25 vs AW26), so the
// Season preview reads "Mixed values" and every other field (Group / Brand / Fit Type / Fabric
// Notes) shows its single common value — a clean 1-mixed-field correspondence.
const mixedPillAlpha = base(96500, 'PCLProbe MixedPill Alpha', {
  season: SEASON_AW25,
  customFields: [fitTypeCf(7031, 'Slim'), textCf(701, 'Shared cotton weave')],
});
const mixedPillBravo = base(96501, 'PCLProbe MixedPill Bravo', {
  season: SEASON_AW26, // the ONLY difference from Alpha
  customFields: [fitTypeCf(7031, 'Slim'), textCf(701, 'Shared cotton weave')],
});

// --- 754730 / 754731 — the modal Search box filters BOTH the General and Custom Fields sections -
const searchFieldsAlpha = base(96502, 'PCLProbe SearchFields Alpha', {
  customFields: [fitTypeCf(7031, 'Slim'), textCf(701, 'Search probe note')],
});
const searchFieldsBravo = base(96503, 'PCLProbe SearchFields Bravo', {
  customFields: [fitTypeCf(7031, 'Slim'), textCf(701, 'Search probe note')],
});

// --- 754733 / 754734 — Confirm step: impacted count/list, changes panel shows only modified -----
// Charlie is seeded but LEFT UNSELECTED by the test, so the impacted list can be asserted to omit
// it (correspondence: exactly the selected styles, no more).
const confirmAlpha = base(96504, 'PCLProbe Confirm Alpha', {
  customFields: [textCf(701, 'Confirm probe A')],
});
const confirmBravo = base(96505, 'PCLProbe Confirm Bravo', {
  customFields: [textCf(701, 'Confirm probe B')],
});
const confirmCharlie = base(96506, 'PCLProbe Confirm Charlie', {
  customFields: [textCf(701, 'Confirm probe C')],
});

// --- 754728 / 754729 — export validation. Both Ready-for-Export so the mandatory-for-export guard
// is armed; the mandatory CF ("Export Note", id 90) is layered per-spec via `apiTransform` on the
// meta endpoint and left EMPTY here.
const exportReadyA = base(96507, 'PCLProbe Export Ready A', { readyForExport: true });
const exportReadyB = base(96508, 'PCLProbe Export Ready B', { readyForExport: true });

// --- 754735 / 754736 / 754737 — activity log (mock-only `GET /api/styles/:id/audit-log`). Each
// pair seeds DISTINCT per-style values so a leaked/reused (non-per-id) response would be visible in
// the audit entry's `oldValue`.
// Updated: distinct starting Fabric Notes per style (the field the test changes).
const logUpdatedA = base(96510, 'PCLProbe LogUpdated A', {
  customFields: [textCf(701, 'Old note A 96510'), fitTypeCf(7031, 'Slim')],
});
const logUpdatedB = base(96511, 'PCLProbe LogUpdated B', {
  customFields: [textCf(701, 'Old note B 96511'), fitTypeCf(7031, 'Slim')],
});
// Added: Alpha Field (711) empty on both; distinct Season per style is the isolation discriminator.
const logAddedA = base(96512, 'PCLProbe LogAdded A', { season: SEASON_AW25 });
const logAddedB = base(96513, 'PCLProbe LogAdded B', { season: SEASON_AW26 });
// Removed: Fit Type set on both (cleared by the test); distinct Season per style discriminates.
const logRemovedA = base(96514, 'PCLProbe LogRemoved A', {
  season: SEASON_AW25,
  customFields: [fitTypeCf(7031, 'Slim')],
});
const logRemovedB = base(96515, 'PCLProbe LogRemoved B', {
  season: SEASON_AW26,
  customFields: [fitTypeCf(7031, 'Slim')],
});

export const stylesPreviewConfirmLogFixtures: StyleRow[] = [
  mixedPillAlpha,
  mixedPillBravo,
  searchFieldsAlpha,
  searchFieldsBravo,
  confirmAlpha,
  confirmBravo,
  confirmCharlie,
  exportReadyA,
  exportReadyB,
  logUpdatedA,
  logUpdatedB,
  logAddedA,
  logAddedB,
  logRemovedA,
  logRemovedB,
];
